import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { fetchAPI } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Clock, MapPin } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const EXEMPT_PREFIXES = [
  "/login", "/register", "/forgot-password", "/reset-password", "/logout",
  "/notifications", "/settings", "/s/",
];

function isExemptPath(fullLoc: string): boolean {
  // fullLoc is wouter location (path only). We also need to consult window.location.search
  // because wouter's location does not include query string.
  const search = typeof window !== "undefined" ? window.location.search : "";
  // Only the approval-queue intent on /orders is exempt — other /orders/* subpages
  // and /orders without the pending/forMe intent are still blocked by the modal.
  // /orders is exempt when the user is on the pending queue (the explicit
  // escape hatch promised by the task), on their own approval queue
  // (forMe=1), or — for admins / orders.view_all — drilling into another
  // approver's queue (forApproverId). All other /orders/* and /orders without
  // a pending intent stay blocked so the nudge keeps teeth.
  if (fullLoc === "/orders") {
    const params = new URLSearchParams(search);
    if (
      params.get("forMe") === "1" ||
      params.get("forApproverId") ||
      params.get("status") === "pending"
    ) {
      return true;
    }
    return false;
  }
  return EXEMPT_PREFIXES.some(prefix => fullLoc.startsWith(prefix));
}

interface PendingForMe {
  enabled: boolean;
  graceMinutes: number;
  count: number;
  overGraceCount: number;
  oldestAgeMinutes: number;
  affectedLocations: string[];
  orders: Array<{ id: string; orderNumber: string; toLocationName: string; ageMinutes: number }>;
  snooze: { expiresAt: string } | null;
  snoozesUsed24h: number;
  snoozeCap: number;
  snoozeMinutes: number;
  shouldLock: boolean;
  canApprove: boolean;
}

function formatAge(mins: number): string {
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h ${mins % 60}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

export function SoftLockProvider() {
  const { t } = useTranslation();
  const { user, currentWorkspace, logout } = useAuth();
  const [location, setLocation] = useLocation();
  const qc = useQueryClient();
  const { toast } = useToast();
  const workspaceId = currentWorkspace?.id;
  const [lastFiredKey, setLastFiredKey] = useState<string | null>(null);

  const isExempt = useMemo(() => isExemptPath(location), [location]);

  const enabled = !!user && !!workspaceId;

  const { data } = useQuery<PendingForMe>({
    queryKey: ["/api/orders/pending-for-me", workspaceId],
    queryFn: () => fetchAPI(`/orders/pending-for-me?workspaceId=${workspaceId}`),
    enabled,
    refetchInterval: 2 * 60 * 1000,
    refetchOnWindowFocus: true,
    retry: false,
  });

  const snoozeMut = useMutation({
    mutationFn: () => fetchAPI(`/orders/approval-snooze`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-workspace-id": workspaceId! },
      body: JSON.stringify({ workspaceId, sourcePath: location }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/orders/pending-for-me"] });
      toast({ title: t("notifMisc.snoozed"), description: t("notifMisc.snoozedDesc") });
    },
    onError: (e: any) => toast({ title: t("notifMisc.couldNotSnooze"), description: e.message, variant: "destructive" }),
  });

  const open = !!data && data.shouldLock && !isExempt;

  // Record lock-fired event once per "show" (count+oldest combo) to avoid spam
  useEffect(() => {
    if (!open || !data || !workspaceId) return;
    const key = `${data.count}|${Math.floor(data.oldestAgeMinutes / 5)}`;
    if (key === lastFiredKey) return;
    setLastFiredKey(key);
    fetchAPI(`/orders/approval-lock-event`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-workspace-id": workspaceId },
      body: JSON.stringify({ workspaceId, pendingCount: data.count, oldestAgeMinutes: data.oldestAgeMinutes }),
    }).catch(() => {});
  }, [open, data, workspaceId, lastFiredKey]);

  if (!enabled || !data) return null;

  const snoozeRemaining = Math.max(0, data.snoozeCap - data.snoozesUsed24h);
  const canSnooze = snoozeRemaining > 0;

  return (
    <Dialog open={open} onOpenChange={() => { /* not dismissible */ }}>
      <DialogContent
        className="max-w-lg [&>button]:hidden"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        data-testid="dialog-soft-lock"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700">
            <AlertTriangle className="h-5 w-5" />
            {t("notifMisc.ordersWaiting")}
          </DialogTitle>
          <DialogDescription>
            {t("notifMisc.reviewPending")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="rounded-md border bg-amber-50 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{t("notifMisc.ordersPendingApproval")}</span>
              <span className="text-2xl font-bold text-amber-700" data-testid="text-pending-count">{data.count}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              {t("notifMisc.oldest")} <span className="font-medium text-foreground" data-testid="text-oldest-age">{formatAge(data.oldestAgeMinutes)}</span>
              <span className="text-xs">{t("notifMisc.gracePeriod", { age: formatAge(data.graceMinutes) })}</span>
            </div>
            {data.affectedLocations.length > 0 && (
              <div className="flex items-start gap-2 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span className="break-words">{data.affectedLocations.slice(0, 5).join(", ")}{data.affectedLocations.length > 5 ? ` +${data.affectedLocations.length - 5}` : ""}</span>
              </div>
            )}
          </div>

          {data.orders.length > 0 && (
            <div className="max-h-40 overflow-y-auto rounded-md border text-sm">
              <table className="w-full">
                <tbody>
                  {data.orders.slice(0, 8).map(o => (
                    <tr key={o.id} className="border-b last:border-0" data-testid={`row-pending-${o.id}`}>
                      <td className="p-2 font-mono text-xs">{o.orderNumber}</td>
                      <td className="p-2 text-xs text-muted-foreground">{o.toLocationName}</td>
                      <td className="p-2 text-xs text-end">{formatAge(o.ageMinutes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            {t("notifMisc.snoozesRemainingToday")} <span className="font-medium">{snoozeRemaining}/{data.snoozeCap}</span>
          </p>
        </div>

        <DialogFooter className="gap-2 sm:gap-2 flex-wrap">
          <Button
            variant="ghost"
            size="sm"
            onClick={async () => {
              try { await logout(); } catch { /* fall through to hard redirect */ }
              window.location.href = "/login";
            }}
            data-testid="button-soft-lock-logout"
          >
            {t("notifMisc.logOut")}
          </Button>
          <div className="flex-1" />
          <Button
            variant="outline"
            disabled={!canSnooze || snoozeMut.isPending}
            onClick={() => snoozeMut.mutate()}
            data-testid="button-snooze"
          >
            {t("notifMisc.snoozeBtn", { minutes: data.snoozeMinutes })}
          </Button>
          <Button
            onClick={() => setLocation("/orders?status=pending&forMe=1")}
            data-testid="button-open-approval-queue"
          >
            {t("notifMisc.openApprovalQueue")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
